import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { getCroppedImageBlobFromElement } from '@/lib/cropImage';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

type ImageCropModalProps = {
  open: boolean;
  imageSrc: string | null;
  title?: string;
  description?: string;
  confirmLabel?: string;
  aspect?: number;
  circularCrop?: boolean;
  outputFileName?: string;
  onClose: () => void;
  onConfirm: (file: File) => Promise<void>;
  loading?: boolean;
};

const DEFAULT_CROP_SIZE_PERCENT = 85;

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: DEFAULT_CROP_SIZE_PERCENT },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export function ImageCropModal({
  open,
  imageSrc,
  title = 'Ajustar foto',
  description = 'Arraste e redimensione a área de recorte sobre a foto.',
  confirmLabel = 'Salvar foto',
  aspect = 1,
  circularCrop = false,
  outputFileName = 'cover.jpg',
  onClose,
  onConfirm,
  loading = false,
}: ImageCropModalProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [imageError, setImageError] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !imageSrc) {
      setCrop(undefined);
      setImageError(false);
      setConfirmError('');
      setConfirming(false);
    }
  }, [open, imageSrc]);

  function applyDefaultCrop(img: HTMLImageElement) {
    const { width, height } = img;
    if (!width || !height) return;
    setCrop(centerAspectCrop(width, height, aspect));
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    applyDefaultCrop(e.currentTarget);
  }

  useLayoutEffect(() => {
    if (!open || !imageSrc) return;
    const img = imgRef.current;
    if (img?.complete && img.width > 0 && img.height > 0) {
      applyDefaultCrop(img);
    }
  }, [open, imageSrc, aspect]);

  async function handleConfirm() {
    const image = imgRef.current;
    if (!imageSrc || !image || !crop?.width || !crop?.height || confirming) return;

    setConfirmError('');
    setConfirming(true);
    try {
      const pixelCrop: PixelCrop = convertToPixelCrop(crop, image.width, image.height);
      const blob = await getCroppedImageBlobFromElement(image, pixelCrop);
      const file = new File([blob], outputFileName, { type: 'image/jpeg' });
      await onConfirm(file);
    } catch (err) {
      setConfirmError(
        err instanceof Error ? err.message : 'Não foi possível recortar a imagem. Tente novamente.',
      );
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading || confirming}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={loading || confirming}
            disabled={!imageSrc || !crop?.width || imageError}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-manejai-bark/70">{description}</p>
        {imageError && (
          <p className="text-sm text-red-600">
            Não foi possível carregar a imagem. Use JPG ou PNG.
          </p>
        )}
        {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

        <div className="max-h-[min(60vh,420px)] overflow-auto rounded-xl bg-manejai-sand/30 p-2">
          {imageSrc && open && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={aspect}
              circularCrop={circularCrop}
              ruleOfThirds
              keepSelection
            >
              <img
                key={imageSrc}
                ref={imgRef}
                src={imageSrc}
                alt="Recorte"
                crossOrigin={imageSrc.startsWith('http') ? 'anonymous' : undefined}
                onLoad={onImageLoad}
                onError={() => setImageError(true)}
                style={{ display: 'block', maxWidth: '100%', maxHeight: 'min(56vh, 400px)' }}
              />
            </ReactCrop>
          )}
        </div>
      </div>
    </Modal>
  );
}
